import { BlockType } from '../../models/index';
import { getBlockModelById, getBlockIndexById, getParentBlocksArray } from '../../common/utils/block';
import { generateUniqueId, decoupleReference, getInverseStyle } from '../../common/utils/common';
import * as constants from '../../common/constant';
import { BlockFactory } from './block-factory';
/**
 * Service responsible for core block-related logics that updates model
 */
export class BlockService {
    constructor(blocks) {
        this.blocks = blocks;
    }
    /**
     * Adds a new block to the provided blocks array
     *
     * @param {IAddBlockOptions} options - Options for creating the block
     * @returns {BlockModel} - The newly added block model
     * @hidden
     */
    addBlock(options) {
        const targetBlockModel = options.targetBlockId ? getBlockModelById(options.targetBlockId, this.blocks) : null;
        const insertArray = targetBlockModel ? getParentBlocksArray(targetBlockModel.id, this.blocks) : this.blocks;
        const indexToInsert = this.getIndexToAdjust(insertArray, targetBlockModel, options.isAfter);
        insertArray.splice(indexToInsert, 0, options.block);
        return options.block;
    }
    /**
     * Removes a block model from the blocks array
     *
     * @param {IRemoveBlockOptions} options - Options for removing the block
     * @returns {{ removedBlock: BlockModel, blockIndex: number }} The removed block and its index
     * @hidden
     */
    removeBlock({ blockId }) {
        const blockModel = getBlockModelById(blockId, this.blocks);
        if (!blockModel) {
            return { removedBlock: null, blockIndex: -1 };
        }
        const containerArray = getParentBlocksArray(blockId, this.blocks);
        const blockIndex = getBlockIndexById(blockId, this.blocks);
        const [removedBlock] = containerArray.splice(blockIndex, 1);
        return { removedBlock, blockIndex };
    }
    /**
     * Updates a block with the provided properties
     *
     * @param {string} blockId - The ID of the block to update
     * @param {Partial<BlockModel>} properties - The properties to update
     * @param {boolean} isUndoRedoAction Whether the action is an undo/redo action
     * @returns {BlockModel} Updated block and blocks array
     * @hidden
     */
    updateBlock(blockId, properties) {
        const blockModel = getBlockModelById(blockId, this.blocks);
        if (!blockModel) {
            return null;
        }
        const updatedBlock = this.mergeBlockProperties(blockModel, properties);
        const containerArray = getParentBlocksArray(blockId, this.blocks);
        const blockIndex = getBlockIndexById(blockId, containerArray);
        containerArray.splice(blockIndex, 1, updatedBlock);
        return updatedBlock;
    }
    /**
     * Duplicates the given block model
     *
     * @param {IDuplicateBlockOptions} options - Options for duplicating the block
     * @returns {BlockModel} The duplicated block model or null
     * @hidden
     */
    duplicateBlock({ blockId }) {
        const blockModel = getBlockModelById(blockId, this.blocks);
        if (!blockModel) {
            return null;
        }
        const containerArray = getParentBlocksArray(blockId, this.blocks);
        const blockIndex = getBlockIndexById(blockId, containerArray);
        const blockToClone = containerArray[blockIndex];
        const clonedBlock = decoupleReference(blockToClone);
        const duplicatedBlockModel = this.generateNewIdsForBlock(clonedBlock);
        return duplicatedBlockModel;
    }
    /**
     * Moves the given block model to a new position
     *
     * @param {IMoveBlockOptions} options - Options for moving the block}
     * @returns {void}
     * @hidden
     */
    moveBlocks(options) {
        const { blockIds, toBlockId, isMovingUp = false } = options;
        const toBlockModel = getBlockModelById(toBlockId, this.blocks);
        if (!toBlockModel) {
            return [];
        }
        // Collect information about blocks to move
        const fromEntries = this.gatherBlocksInfoForMove(blockIds);
        if (fromEntries.length === 0) {
            return [];
        }
        // Remove blocks from their original positions
        const movedBlocks = this.removeBlocksForMove(fromEntries);
        // Insert blocks at the target position
        this.insertBlocksAtTarget(movedBlocks, toBlockId, isMovingUp);
        return movedBlocks;
    }
    /**
     * Handles the indent/outdent of block
     *
     * @param {IIndentBlockOptions} options - Options to indent/unindent block
     * @returns {BlockModel} - The updated block model
     * @hidden
     */
    applyIndentation(options) {
        const { blockId, shouldDecrease } = options;
        const blockModel = getBlockModelById(blockId, this.blocks);
        if (!blockModel) {
            return null;
        }
        const blockIndex = getBlockIndexById(blockId, this.blocks);
        const parentBlock = getBlockModelById(blockModel.parentId, this.blocks);
        if (shouldDecrease) {
            if (blockModel.indent > 0) {
                blockModel.indent--;
            }
        }
        else {
            // Indent - only allow if previous block is at same or higher level
            const adjacentBlockModel = parentBlock
                ? parentBlock.properties.children[blockIndex - 1]
                : this.blocks[blockIndex - 1];
            if (adjacentBlockModel) {
                if (blockModel.indent <= adjacentBlockModel.indent) {
                    blockModel.indent++;
                }
            }
            else {
                blockModel.indent++;
            }
        }
        return blockModel;
    }
    /**
     * Handles the line break of block
     *
     * @param {number} insertOffset - The offset at which to insert the line break
     * @param {ContentModel} contentModel - The content model to update
     * @returns {void}
     * @hidden
     */
    applyLineBreak(insertOffset, contentModel) {
        if (insertOffset < 0 || !contentModel) {
            return;
        }
        contentModel.content =
            contentModel.content.substring(0, insertOffset) +
                '\n' +
                contentModel.content.substring(insertOffset);
    }
    /**
     * Updates the block content with given data
     *
     * @param {string} blockId The id of the block
     * @param {ContentModel[]} content The content to update
     * @returns {void}
     * @hidden
     */
    updateContent(blockId, content) {
        const block = getBlockModelById(blockId, this.blocks);
        block.content = content;
    }
    /**
     * Toggles formatting style on content model
     *
     * @param {ContentModel} content - The content model to update
     * @param {any} format - The format to toggle (eg. Bold, Italic)
     * @param {boolean} force - Whether to force the format intent
     * @param {boolean} formatIntent - Whether to apply or remove the format
     * @param {string} value - The value for non-boolean styles
     * @returns {ContentModel} - The updated content model
     * @hidden
     */
    toggleContentStyles(content, format, force, formatIntent, value) {
        const updatedContent = Object.assign({}, content);
        const booleanStyles = ['bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript', 'uppercase', 'lowercase', 'inlineCode'];
        const togglePairs = ['superscript', 'subscript', 'uppercase', 'lowercase'];
        const isBooleanStyle = booleanStyles.indexOf(format) !== -1;
        const isTogglePair = togglePairs.indexOf(format) !== -1;
        if (!updatedContent.properties) {
            updatedContent.properties = { styles: {} };
        }
        if (!updatedContent.properties.styles) {
            updatedContent.properties.styles = {};
        }
        const styles = updatedContent.properties.styles;
        if (isBooleanStyle) {
            let newValue = !styles[format];
            if (force) {
                newValue = formatIntent;
            }
            if (newValue) {
                styles[format] = true;
            }
            else {
                delete styles[format];
            }
            if (isTogglePair) {
                const oppositeFormat = getInverseStyle(format);
                delete styles[oppositeFormat];
            }
        }
        else {
            if (value) {
                styles[format] = value;
            }
            else {
                delete styles[format];
            }
        }
        return updatedContent;
    }
    /**
     * Applies link formatting to content model
     *
     * @param {ContentModel} content - The content model to update
     * @param {LinkData} linkData - The link data containing URL, text, and other properties
     * @returns {ContentModel} - The updated content model
     * @hidden
     */
    applyLinkFormatting(content, linkData) {
        let updatedContent;
        const textContent = linkData.text ? linkData.text : content.content;
        if (!linkData.shouldRemoveLink) {
            updatedContent = BlockFactory.createLinkContent({ content: textContent }, Object.assign({}, content.properties, { url: linkData.url }));
        }
        else {
            delete content.properties.url;
            updatedContent = BlockFactory.createTextContent({
                content: textContent
            }, content.properties);
        }
        return updatedContent;
    }
    /**
     * Assigns parent ID to multiple blocks
     *
     * @param {BlockModel[]} blocks - The blocks to update
     * @param {string} parentId - The parent ID to assign
     * @returns {void}
     * @hidden
     */
    assignParentIdToBlocks(blocks, parentId) {
        blocks.forEach((block) => {
            block.parentId = parentId;
        });
    }
    /**
     * Gets the index to adjust the block based on the given targetBlock.
     *
     * @param {BlockModel[]} insertionArray The target collection of blocks to insert.
     * @param {BlockModel} targetBlock The block after which the new block should be inserted.
     * @param {boolean} isAfter Specifies whether the new block should be inserted after the targetBlock.
     * @returns {number} The index at which the new block should be inserted.
     * @hidden
     */
    getIndexToAdjust(insertionArray, targetBlock, isAfter = true) {
        let insertIndex = insertionArray.length;
        if (targetBlock) {
            const afterBlockIndex = getBlockIndexById(targetBlock.id, insertionArray);
            if (afterBlockIndex !== -1) {
                insertIndex = afterBlockIndex + (isAfter ? 1 : 0);
            }
        }
        return insertIndex;
    }
    /**
     * Gathers information about blocks to be moved
     *
     * @param {string[]} fromBlockIds Array of block IDs to move
     * @returns {IFromBlockData[]} Array of block information objects
     * @hidden
     */
    gatherBlocksInfoForMove(fromBlockIds) {
        const entries = [];
        for (const fromBlockId of fromBlockIds) {
            const blockModel = getBlockModelById(fromBlockId, this.blocks);
            const index = getBlockIndexById(fromBlockId, this.blocks);
            if (index >= 0 && blockModel) {
                const parent = getBlockModelById(blockModel.parentId, this.blocks);
                entries.push({ blockId: fromBlockId, model: blockModel, index, parent });
            }
        }
        return entries.sort((a, b) => b.index - a.index);
    }
    /**
     * Removes blocks from their current position in the model
     *
     * @param {IFromBlockData[]} fromEntries Array of block information objects
     * @returns {IFromBlockData[]} Array of removed block models
     * @hidden
     */
    removeBlocksForMove(fromEntries) {
        const allFromModels = [];
        // Splice safely from the highest index to avoid index shifts
        for (const entry of fromEntries) {
            const { blockId, index, parent } = entry;
            const containerArray = getParentBlocksArray(blockId, this.blocks);
            const [moved] = containerArray.splice(index, 1);
            // allFromModels has the original indexes and parentid before mutation in reverse order
            allFromModels.push({ blockId: blockId, model: moved, parent, index });
        }
        return allFromModels;
    }
    /**
     * Inserts blocks at the target position in the model
     *
     * @param {IFromBlockData[]} movedBlocks Array of block models to insert
     * @param {string} toBlockId Target block ID
     * @param {boolean} isMovingUp Whether blocks are moving up or down
     * @returns {void}
     * @hidden
     */
    insertBlocksAtTarget(movedBlocks, toBlockId, isMovingUp) {
        const toBlockIndex = getBlockIndexById(toBlockId, this.blocks);
        if (toBlockIndex < 0) {
            return null;
        }
        const insertToArray = getParentBlocksArray(toBlockId, this.blocks);
        const toBlockModel = getBlockModelById(toBlockId, insertToArray);
        const toParentBlock = getBlockModelById(toBlockModel.parentId, this.blocks);
        const insertIndex = getBlockIndexById(toBlockId, insertToArray) + (isMovingUp ? 0 : 1);
        for (const entry of movedBlocks) {
            entry.model.parentId = toParentBlock ? toParentBlock.id : '';
            insertToArray.splice(insertIndex, 0, entry.model);
        }
    }
    /**
     * Replaces a block at specific index in parent's children or root blocks array
     *
     * @param {string} originalBlockId - The ID of the block to replace
     * @param {BlockModel} newBlock - The new block to insert
     * @returns {void}
     * @hidden
     */
    replaceBlock(originalBlockId, newBlock) {
        const originalBlock = getBlockModelById(originalBlockId, this.blocks);
        if (!originalBlock) {
            return;
        }
        const insertToArray = getParentBlocksArray(originalBlockId, this.blocks);
        const indexToReplace = getBlockIndexById(originalBlock.id, insertToArray);
        insertToArray.splice(indexToReplace, 1, newBlock);
    }
    /**
     * Generates new IDs for the block and its content.
     *
     * @param {BlockModel} block The block model to generate new IDs for.
     * @param {string} parentId The parent ID of the block.
     * @returns {void} The block model with new IDs.
     * @hidden
     */
    generateNewIdsForBlock(block, parentId) {
        block.id = generateUniqueId(constants.BLOCK_ID_PREFIX);
        if (parentId) {
            block.parentId = parentId;
        }
        if (block.blockType === BlockType.Table) {
            return this.generateNewIdsForTableBlock(block);
        }
        let children = (block.properties && block.properties.children)
            ? block.properties.children
            : [];
        if (children && children.length > 0) {
            children = children.map((child) => this.generateNewIdsForBlock(child, block.id));
        }
        return block;
    }
    generateNewIdsForTableBlock(tableBlock) {
        const props = tableBlock.properties;
        // 1. Regenerate all column IDs + store mapping
        const columnIdMap = new Map();
        props.columns = props.columns.map((col) => {
            const newId = generateUniqueId('col_');
            columnIdMap.set(col.id, newId);
            return Object.assign({}, col, { id: newId });
        });
        // 2. Regenerate row IDs and cell IDs + fix columnId references
        props.rows = props.rows.map((row) => {
            const newRowId = generateUniqueId('row_');
            const newCells = row.cells.map((cell) => {
                const newCellId = generateUniqueId('cell_');
                const newColumnId = columnIdMap.get(cell.columnId) || cell.columnId;
                // Regenerate IDs for all blocks inside this cell
                const newBlocks = cell.blocks.map((innerBlock) => this.generateNewIdsForBlock(innerBlock, newCellId));
                return Object.assign({}, cell, { id: newCellId, columnId: newColumnId, blocks: newBlocks });
            });
            return Object.assign({}, row, { id: newRowId, cells: newCells });
        });
        return tableBlock;
    }
    /**
     * Merges partial block properties with an existing block
     *
     * @param {BlockModel} block - Original block model
     * @param {Partial<BlockModel>} properties - Partial properties to merge
     * @param {boolean} isUndoRedoAction Whether the action is an undo/redo action
     * @returns {BlockModel} Merged block model
     * @hidden
     */
    mergeBlockProperties(block, properties) {
        const clonedBlock = decoupleReference(block);
        const mergedBlock = Object.assign({}, clonedBlock);
        // Merge block-level properties
        Object.keys(properties).forEach((key) => {
            if (key === 'content' && properties.content) {
                mergedBlock.content = properties.content.map((newContent) => {
                    return BlockFactory.createContentFromPartial(newContent);
                });
            }
            else if ((key === 'properties' && properties.properties)) {
                mergedBlock[key] = this.mergePrimitiveTypes(clonedBlock[key], properties[key]);
            }
            else {
                mergedBlock[key] = properties[key];
            }
        });
        return mergedBlock;
    }
    /**
     * Merges a primitive type model with it's partial updates
     *
     * @param {any} existing - Existing model
     * @param {Partial<any>} updates - Partial updates
     * @returns {any} Merged model
     * @hidden
     */
    mergePrimitiveTypes(existing, updates) {
        const merged = Object.assign({}, existing);
        Object.keys(updates).forEach((key) => {
            merged[key] = updates[key];
        });
        return merged;
    }
    /**
     * Gets the editor blocks data
     *
     * @returns {BlockModel[]} The editor blocks data
     * @hidden
     */
    getBlocks() {
        return this.blocks;
    }
    /**
     * Sets the editor blocks data with the given blocks
     *
     * @param {BlockModel[]} blocks The blocks to set for the editor
     * @returns {void}
     * @hidden
     */
    setBlocks(blocks) {
        this.blocks = blocks;
    }
}
